import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/lib/AuthContext';
import * as api from '@/lib/api';
import logger from '../lib/logger';

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let tries = 0;

    const poll = async () => {
      if (cancelled) return;
      if (tries >= 10) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await api.getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        tries += 1;
        timer = setTimeout(poll, 2000);
      } catch (error) {
        logger.error('Error checking payment status:', error);
        if (!cancelled) setStatus('error');
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refreshUser]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-heading font-bold mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </>
        );
      
      case 'success':
        return (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for subscribing. Your account has been upgraded.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="glow-primary">
              Go to Dashboard
            </Button>
          </>
        );
      
      case 'error':
      case 'expired':
      case 'timeout':
        return (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold mb-2">
              {status === 'expired' ? 'Session Expired' : 'Payment Failed'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {status === 'timeout' 
                ? 'Payment verification timed out. Please check your email for confirmation.'
                : 'Something went wrong. Please try again.'}
            </p>
            <Button onClick={() => navigate('/billing')} variant="outline">
              Back to Billing
            </Button>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="bg-card border-border max-w-md w-full">
        <CardContent className="py-12 text-center">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
}
